import { Component, EventEmitter, Input, Output, OnChanges, OnInit } from '@angular/core';
import { Modal } from 'flowbite';
import { IBookingDetails } from 'src/app/models/booking/bookingDetails';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { UsersService } from 'src/app/services';
import { images } from 'src/images';
import { SeatProfile, TicketingType } from 'src/app/models/enums';
import { SeatNeed } from 'src/app/models/enums/seatNeed';
import { IBookingSeat, IPreferredSeat } from 'src/app/models';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';

@Component({
    selector: 'app-modal-change-booking-seats',
    templateUrl: './modal-change-booking-seats.component.html',
    styleUrl: './modal-change-booking-seats.component.scss',
    standalone: false
})
export class ModalChangeBookingSeatsComponent implements OnInit, OnChanges {
  @Input() id: string;
  @Input() modal: Modal;
  @Input() booking: IBookingDetails;
  @Input() editable: boolean;
  @Input() needsConfirmation?: boolean = false;
  @Input() showNotify: boolean = true;
  @Output() action = new EventEmitter<any>();
  @Output() report = new EventEmitter<any>();

  public confirmChange = false;
  public notify = true;
  public recomputePrice = true;

  public images = images;
  public preferredSeats: IPreferredSeat[] = [];
  public filteredNames: string[] = [];
  public profiles = Object.values(SeatProfile);
  public profileImages!: Record<SeatProfile, string>;
  public TooltipPosition = TooltipPosition;

  public needsMap: Record<SeatProfile, SeatNeed[]> = {
    [SeatProfile.Regular]: [SeatNeed.None, SeatNeed.Step],
    [SeatProfile.Minor]: [SeatNeed.None, SeatNeed.Chair_0, SeatNeed.Chair_1, SeatNeed.Chair_2, SeatNeed.Chair_3],
    [SeatProfile.Pet]: [SeatNeed.None, SeatNeed.Carrier],
    [SeatProfile.Pregnant]: [SeatNeed.None, SeatNeed.Step],
    [SeatProfile.Dependant]: [SeatNeed.None, SeatNeed.Step],
    [SeatProfile.Blind]: [SeatNeed.None, SeatNeed.VerbalGuidance],
    [SeatProfile.Deaf]: [SeatNeed.None, SeatNeed.WrittenGuidance]
  };

  public needs = Object.values(SeatNeed)
  public filteredNeeds: string[] = [];

  constructor(
    public literalService: LiteralService,
    private userService: UsersService
  ) { }

  get showRecomputePriceToggle(): boolean {
    if (!this.booking) return false;
    return !!this.booking.ticketingType && this.booking.ticketingType !== TicketingType.None;
  }

  async ngOnInit() {
    this.filteredNeeds = this.needs;
    this.filteredNames = this.preferredSeats && this.preferredSeats.map(it => it.name);
    if (this.booking && this.booking.userId) {
      this.preferredSeats = await this.userService.getPreferredSeats(this.booking.userId)
      this.filterNames('')
    }
  }

  async ngOnChanges() {
    if (this.booking && this.booking.userId) {
      this.preferredSeats = await this.userService.getPreferredSeats(this.booking.userId)
      this.filterNames('')
    }
    this.confirmChange = false;
  }

  trackBySeat(index: number, seat: IBookingSeat) {
    return seat.id;
  }

  change(event: any) {
    if (event.type == "prm" || event.type == "pax") {
      const typeIsPrm = event.type === "prm";

      if (event.value != null) {
        const typeIsPrm = event.type === "prm";
        const currentSeats = this.booking.bookingSeats.filter(s => s.prm === typeIsPrm);
        const currentCount = currentSeats.length;
        const newCount = Number(event.value);

        if (newCount > currentCount) {
          // Add seats at the end
          const seatsToAdd = newCount - currentCount;
          const newSeats = Array.from({ length: seatsToAdd }, () => ({
            exitStop: { id: this.booking.destination.serviceStopId, name: this.booking.destination.name },
            prm: typeIsPrm,
            profile: SeatProfile.Regular,
            needRequested: false,
            noShow: false
          }));
          this.booking.bookingSeats = [...this.booking.bookingSeats, ...newSeats];
        } else if (newCount < currentCount) {
          // Remove seats from the end, keeping the first ones
          let toRemove = currentCount - newCount;
          for (let i = this.booking.bookingSeats.length - 1; i >= 0 && toRemove > 0; i--) {
            if (this.booking.bookingSeats[i].prm === typeIsPrm) {
              this.booking.bookingSeats.splice(i, 1);
              toRemove--;
            }
          }
        }
      } else {
        // existing plus/minus logic
        if (event.key == "plus") {
          this.booking.bookingSeats = [
            ...this.booking.bookingSeats,
            {
              exitStop: { id: this.booking.destination.serviceStopId, name: this.booking.destination.name },
              prm: typeIsPrm,
              profile: SeatProfile.Regular,
              needRequested: false,
              noShow: false
            }
          ];
        }
        if (event.key == "minus") {
          const idx = [...this.booking.bookingSeats]
            .map((s, i) => (s.prm === typeIsPrm ? i : -1))
            .filter(i => i !== -1)
            .pop();
          if (idx !== undefined) {
            this.booking.bookingSeats = this.booking.bookingSeats.filter((_, i) => i !== idx);
          }
        }
      }
    }
  }

  delete(index: number) {
    this.booking.bookingSeats = this.booking.bookingSeats.filter((_, i) => i != index);
  }

  reportSeat(seat: IBookingSeat) {
    this.report.emit({ seatId: seat.id, callback: (success: boolean) => {
      if (success) {
        seat.noShow = !seat.noShow;
      }
    }});
  }

  filterNames(value: string): void {
    const filterValue = value?.toLowerCase() || '';
    this.filteredNames = this.preferredSeats && this.preferredSeats.map(it => it.name).filter(n => n
        .toLowerCase()
        .includes(filterValue)
    );
  }

  // This is called whenever input changes
  onNameInputChange(event: any) {
    this.filterNames(event.target.value);
  }

  onNameOptionSelected(name: string, index: number) {
    const updatedSeat: IBookingSeat = this.booking.bookingSeats[index];
    updatedSeat.name = name;
    const preferredSeat = this.preferredSeats.find(it => it.name == name);
    if (preferredSeat) {
      updatedSeat.profile = preferredSeat.profile;
      updatedSeat.need = preferredSeat.need;
      updatedSeat.needRequested = preferredSeat.needRequested;
    }
    this.booking.bookingSeats[index] = updatedSeat;
  }

  openPopover(el: HTMLElement, hasItems: boolean) {
    if (!hasItems) {
      this.closePopover(el);
    } else if (!el.matches(':popover-open')) {
      el.showPopover();
    }
  }

  closePopover(el: HTMLElement) {
    if (el.matches(':popover-open')) el.hidePopover();
  }

  filterNeeds(value: string, profile: SeatProfile): void {
    this.filteredNeeds = this.needsMap[profile] || [];
    const filterValue = value?.toLowerCase() || '';
    this.filteredNeeds = this.filteredNeeds.filter(n =>
      this.literalService.get('bookings.seats.need.' + n, false).includes(filterValue)
    );
  }

  // This is called whenever clicking the input
  onNeedFocus(profile: SeatProfile) {
    this.filterNeeds("", profile);
  }

  // This is called whenever input changes
  onNeedInputChange(event: any, profile: SeatProfile) {
    this.filterNeeds(event.target.value, profile);
  }

  // Optional: display function for autocomplete
  displayNeed = (need: string): string => {
    if (!need) return '';
    const needsWhitelist = Object.values(SeatNeed);
    if (needsWhitelist.includes(need as SeatNeed)) {
      return this.literalService.get('bookings.seats.need.' + need, true);
    }
    return need;
  }

  getProfileImage(profile: SeatProfile, isPrm: boolean): string {
    if (isPrm && profile == SeatProfile.Regular) return this.images.prm;
      const imagesMap: Record<SeatProfile, string> = {
      [SeatProfile.Regular]: this.images.pax,
      [SeatProfile.Minor]: this.images.minor,
      [SeatProfile.Pet]: this.images.pet,
      [SeatProfile.Pregnant]: this.images.pregnant,
      [SeatProfile.Dependant]: this.images.dependant,
      [SeatProfile.Blind]: this.images.blind,
      [SeatProfile.Deaf]: this.images.deaf,
      /*[SeatProfile.Luggage]: this.images.luggage,
      [SeatProfile.Package]: this.images.package,
      [SeatProfile.Bike]: this.images.bike,
      [SeatProfile.Scooter]: this.images.scooter,*/
    };
    return imagesMap[profile];
  }

  onCancel() {
    if (this.confirmChange) this.confirmChange = false;
    else this.modal.hide();
  }

  onSubmit() {
    if (this.needsConfirmation && !this.confirmChange) {
      this.confirmChange = true;
    } else {
      this.modal.toggle();
      this.action.emit({booking: this.booking, sendNotification: this.notify, recomputePrice: this.recomputePrice});
    }
  };
}
